import React, { useState, Fragment, useContext } from 'react';
import JumboContentLayout from '@jumbo/components/JumboContentLayout';
import useJumboTheme from '@jumbo/hooks/useJumboTheme';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Form, Formik } from 'formik';
import JumboTextField from '@jumbo/components/JumboFormik/JumboTextField';
import { ApiService } from 'app/servicesTwo/ApiService';
import { useLocation } from 'react-router-dom';
import useSwalWrapper from '@jumbo/vendors/sweetalert2/hooks';
import { PermissionContext } from 'app/contexts/PermissionContext';

const ModalChangeVolunteerActivityStatus = ({
  data,
  handleClose,
  getTeamVolunteer,
  getBookTeamsById,
}) => {
  const { theme } = useJumboTheme();
  const location = useLocation();
  const lg = useMediaQuery(theme.breakpoints.down('lg'));

  const layoutOptions = React.useMemo(
    () => ({
      sidebar: {
        sx: {
          [theme.breakpoints.up('lg')]: {
            position: 'sticky',
            zIndex: 5,
            top: 96,
            minHeight: 'auto',
          },
          [theme.breakpoints.down('lg')]: {
            display: 'none',
          },
        },
      },
      wrapper: {
        sx: { alignItems: 'flex-start' },
      },
    }),
    [theme]
  );

  const actividadeDo = data?.Atividade_Do_Voluntario;
  const isAtivo = actividadeDo?.status === 'ativo';
  const isAfastado = actividadeDo?.status === 'afastado';

  const initialValues = {
    // Encerramento
    data_fim: actividadeDo?.data_fim ?? '',
    motivo_encerramento: actividadeDo?.motivo_encerramento ?? '',
    encerrar_voluntariado: false,

    // Afastamento
    data_afastamento_fim: actividadeDo?.data_afastamento_fim ?? '',
    motivo_afastamento: actividadeDo?.motivo_afastamento ?? '',
    afastar_voluntario_temporariamente: isAfastado ? true : false,
  };

  const Swal = useSwalWrapper();
  const toast = (variant, message) => {
    const Toast = Swal.mixin({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      onOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer);
        toast.addEventListener('mouseleave', Swal.resumeTimer);
      },
    });
    Toast.fire({
      icon: variant,
      title: message,
      showCloseButton: true,
      closeButtonAriaLabel: 'Fechar',
    });
  };

  const { hasPermission } = useContext(PermissionContext);
  const storedUserId = localStorage.getItem('storedUserId');
  const activityId = actividadeDo?.atividade_do_voluntario_id;

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!activityId) {
      console.error('Atividade não encontrada');
      setSubmitting(false);
      return;
    }

    // ── ENCERRAR ──────────────────────────────────────────────
    if (values.encerrar_voluntariado === true) {
      const result = await Swal.fire({
        title: 'Tem certeza que deseja encerrar?',
        text: 'Não será possível reverter a ação!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim!',
        cancelButtonText: 'Não!',
        reverseButtons: true,
      });

      if (result.value) {
        try {
          await ApiService.put(
            `/atividades-do-voluntario/encerrar/${activityId}`,
            {
              values: {
                data_fim: values.data_fim,
                motivo_encerramento: values.motivo_encerramento,
                id_quem_atualizou: storedUserId,
              },
            }
          );
          toast('success', 'Atuação na atividade encerrada');
          getTeamVolunteer();
          getBookTeamsById();
          handleClose();
        } catch (error) {
          toast('error', 'Ocorreu um erro ao encerrar');
          console.error(error);
        }
      }

      setSubmitting(false);
      return;
    }

    // ── AFASTAR / ATUALIZAR ───────────────────────────────────
    try {
      const payload = {
        // repassa campos obrigatórios que o endpoint update precisa
        area_id: actividadeDo?.area_id,
        departamento_id: actividadeDo?.departamento_id,
        atividade_selecionada_id: actividadeDo?.atividade_voluntario_id,
        local: actividadeDo?.local,
        data_inicio: actividadeDo?.data_inicio,
        complemento_atividade: actividadeDo?.complemento_atividade,
        // afastamento
        afastar_voluntario_temporariamente:
          values.afastar_voluntario_temporariamente,
        data_afastamento_fim: values.afastar_voluntario_temporariamente
          ? values.data_afastamento_fim || undefined
          : undefined,
        motivo_afastamento: values.afastar_voluntario_temporariamente
          ? values.motivo_afastamento || undefined
          : undefined,
        // status derivado
        status: values.afastar_voluntario_temporariamente || values.data_afastamento_fim
          ? 'afastado'
          : 'ativo',
        id_quem_atualizou: storedUserId,
      };

      await ApiService.put(`/atividades-do-voluntario/${activityId}`, {
        values: payload,
      });

      toast(
        'success',
        values.afastar_voluntario_temporariamente
          ? 'Colaborador afastado temporariamente'
          : 'Atividade atualizada com sucesso'
      );
      getTeamVolunteer();
      getBookTeamsById();
      handleClose();
    } catch (error) {
      toast('error', 'Ocorreu um erro ao atualizar');
      console.error(error);
    }

    setSubmitting(false);
  };

  return (
    <JumboContentLayout layoutOptions={layoutOptions}>
      {lg && <Stack spacing={2} direction={'row'} sx={{ mb: 3, mt: -2 }} />}

      <Paper sx={{ p: '30px' }}>
        {/* ── Cabeçalho ── */}
        <Box display='flex' justifyContent='flex-end' mb={1}>
          <Button size='small' variant='contained' onClick={handleClose}>
            Fechar
          </Button>
        </Box>

        <Typography variant={'h2'} textAlign={'center'} mb={1}>
          Editar atividade
        </Typography>

        <Typography variant='h4' textAlign={'center'} fontWeight={'300'} mb={3}>
          Colaborador:{' '}
          <strong style={{ textTransform: 'capitalize' }}>
            {actividadeDo?.Voluntario?.User?.nome}
          </strong>
        </Typography>

        <Formik
          initialValues={initialValues}
          enableReinitialize
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={handleSubmit}
        >
          {({ values, isSubmitting, handleChange }) => (
            <Form style={{ width: '100%' }} noValidate autoComplete='off'>
              {/* ═══ ACCORDION: Encerrar ═══ */}
              <Accordion
                expanded={values.encerrar_voluntariado === true}
                elevation={0}
              >
                <AccordionSummary>
                  <Stack
                    sx={{ width: '100%' }}
                    direction={{ xs: 'column', lg: 'row' }}
                    justifyContent='space-between'
                    alignItems='center'
                    spacing={2}
                  >
                    <Typography sx={{ fontSize: '1.05rem' }}>
                      {isAtivo || isAfastado
                        ? 'Encerrar atuação na atividade?'
                        : 'Atividade Encerrada (Para reativar, cadastre novamente)'}
                    </Typography>

                    {(isAtivo || isAfastado) && (
                      <Stack direction='row' alignItems='center' spacing={1}>
                        <Typography>Não</Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={values.encerrar_voluntariado === true}
                              onChange={handleChange}
                              name='encerrar_voluntariado'
                              disabled={
                                !hasPermission(
                                  'Alocação de Colaboradores',
                                  'editar'
                                )
                              }
                            />
                          }
                          labelPlacement='start'
                        />
                        <Typography>Sim</Typography>
                      </Stack>
                    )}
                  </Stack>
                </AccordionSummary>

                <AccordionDetails>
                  <Grid container spacing={3}>
                    <Grid item xs={6}>
                      <JumboTextField
                        fullWidth
                        name='data_fim'
                        type='date'
                        label='Data Final'
                        value={values.data_fim}
                        sx={{ background: '#ffffff' }}
                        InputLabelProps={{ shrink: true }}
                        disabled={
                          !hasPermission('Alocação de Colaboradores', 'editar')
                        }
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <JumboTextField
                        fullWidth
                        name='motivo_encerramento'
                        label='Motivo'
                        value={values.motivo_encerramento}
                        sx={{ background: '#ffffff' }}
                        InputLabelProps={{ shrink: true }}
                        disabled={
                          !hasPermission('Alocação de Colaboradores', 'editar')
                        }
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {values.encerrar_voluntariado !== true && (
                <Divider variant='middle' />
              )}

              {/* ═══ ACCORDION: Afastar ═══ */}
              {(isAtivo || isAfastado) && (
                <>
                  <Accordion
                    expanded={
                      values.afastar_voluntario_temporariamente === true
                    }
                    elevation={0}
                  >
                    <AccordionSummary>
                      <Stack
                        sx={{ width: '100%' }}
                        direction={{ xs: 'column', lg: 'row' }}
                        justifyContent='space-between'
                        alignItems='center'
                        spacing={2}
                      >
                        <Typography sx={{ fontSize: '1.05rem' }}>
                          Afastar colaborador temporariamente?
                        </Typography>

                        <Stack direction='row' alignItems='center' spacing={1}>
                          <Typography>Não</Typography>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={
                                  values.afastar_voluntario_temporariamente ===
                                  true
                                }
                                onChange={handleChange}
                                name='afastar_voluntario_temporariamente'
                                disabled={
                                  !hasPermission(
                                    'Alocação de Colaboradores',
                                    'editar'
                                  )
                                }
                              />
                            }
                            labelPlacement='start'
                          />
                          <Typography>Sim</Typography>
                        </Stack>
                      </Stack>
                    </AccordionSummary>

                    <AccordionDetails>
                      <Grid container spacing={3}>
                        <Grid item xs={6}>
                          <JumboTextField
                            fullWidth
                            name='data_afastamento_fim'
                            type='date'
                            label='Data Final do Afastamento'
                            value={values.data_afastamento_fim}
                            sx={{ background: '#ffffff' }}
                            InputLabelProps={{ shrink: true }}
                            disabled={
                              !hasPermission(
                                'Alocação de Colaboradores',
                                'editar'
                              )
                            }
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <JumboTextField
                            fullWidth
                            name='motivo_afastamento'
                            label='Motivo do Afastamento'
                            value={values.motivo_afastamento}
                            sx={{ background: '#ffffff' }}
                            InputLabelProps={{ shrink: true }}
                            disabled={
                              !hasPermission(
                                'Alocação de Colaboradores',
                                'editar'
                              )
                            }
                          />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>

                  <Divider variant='middle' />
                </>
              )}

              {/* ═══ BOTÕES ═══ */}
              <Grid item xs={12} sx={{ textAlign: 'center', mt: 3 }}>
                {values.encerrar_voluntariado === true ? (
                  <LoadingButton
                    color='error'
                    type='submit'
                    variant='contained'
                    size='small'
                    loading={isSubmitting}
                    disabled={
                      !hasPermission('Alocação de Colaboradores', 'editar') ||
                      !values.data_fim ||
                      !values.motivo_encerramento
                    }
                  >
                    Encerrar atuação na atividade
                  </LoadingButton>
                ) : (
                  <LoadingButton
                    color='success'
                    type='submit'
                    variant='contained'
                    size='small'
                    loading={isSubmitting}
                    disabled={
                      !hasPermission('Alocação de Colaboradores', 'editar')
                    }
                  >
                    Atualizar
                  </LoadingButton>
                )}
              </Grid>
            </Form>
          )}
        </Formik>
      </Paper>
    </JumboContentLayout>
  );
};

export default ModalChangeVolunteerActivityStatus;
